import { Suspense, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { ResultsFilters, ResultsFormFilters } from './FilterTypes';
import { MemberPendingSection } from './MemberPendingSection';
import { PendingPicksTeaser } from './PendingPicksTeaser';
import { ResultsBreakdowns, ResultsBreakdownsContent } from './ResultsBreakdowns';
import { ResultsReconcileProvider } from './ResultsReconcileContext';
import ResultsTable, { ResultsTableContent } from './ResultsTable';
import { resolveSportIds } from './consts';

// Graded (resolved) results — pending is intentionally excluded so it only
// appears in the pending section, and futures (result === 'pending') drop too.
const GRADED_RESULTS = ['win', 'loss', 'push', 'canceled', 'noAction'] as const;

export function ResultsContents({
  baseUrl,
  userId,
  filters,
}: {
  baseUrl: string;
  form: UseFormReturn<ResultsFormFilters>;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  breakdown?: string;
  title?: string;
}) {
  const { sportId: sportIdBase, startDate, endDate, playType, gameType, free } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  return (
    // Scope both lists (pending + graded) so a grade in one reconciles the
    // other live, no page refresh needed (#1284).
    <ResultsReconcileProvider>
      <Suspense
        fallback={<ResultsBreakdownsContent baseUrl={baseUrl} userId={userId} filters={filters} />}
      >
        <ResultsBreakdowns baseUrl={baseUrl} userId={userId} filters={filters} />
      </Suspense>
      {/* The member's own pending plays, pinned above the graded results. */}
      {userId === 'me' && (
        <MemberPendingSection userId={userId} sportId={sportId} serviceIds={serviceIds} />
      )}
      {/* Public archive only: pending picks teaser, above the graded plays but
          below the charts. */}
      {userId === 'archive' && <PendingPicksTeaser sportIdBase={sportIdBase} />}
      <Suspense fallback={<ResultsTableContent userId={userId} infiniteRequest={null} />}>
        <ResultsTable
          sportId={sportId}
          serviceIds={serviceIds}
          playType={playType}
          gameType={gameType}
          startDate={startDate}
          endDate={endDate}
          userId={userId}
          free={free}
          // On the member's own view, pending plays live in the section above,
          // so keep the main table to graded results only (no duplicates).
          result={userId === 'me' ? GRADED_RESULTS : undefined}
        />
      </Suspense>
    </ResultsReconcileProvider>
  );
}
